import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes, { number } from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Button, BaseModal, pitchType, PitchLocation, LoadingSpinner, ToggleButtons } from 'yankees-react-utils';
import { Approach, Video, useOktaQuery, useOktaMutation } from '../../../data';
import plate from '../../../images/homeplate.png';

const StyledPitchButton = styled(Button)`
    && {
        margin: 5px;
        border-radius: 0.25rem !important;
    }
`;
const StyledErrorMesssage = styled.span`
&& {
    color: red;
    font-style: 'italic';
    text-align: 'center';
}
`;
const StyledMesssage = styled.span`
&& {
    color: blue;
    font-weight: 'bold';
    text-align: 'center';
}
`;
const StyledStartEndContainer = styled(ToggleButtons)`
    && {
        width: 100%;
        margin-left: auto;
        margin-right: auto;
        padding-left: 50px;
        padding-right: 50px;
    }
`;

const ApproachModal = ({ show, container, onSave, onHide, batterId, pitcherId, userName, fxpitchTypes, pitchToUpdate }) => {
    const { t: translate } = useTranslation();
    const defaultObject = { ApproachPitchID: 0, LocationX: 0, LocationY: 0, FXType: null, IsPitchStart: true };
    const [saveWait, setSaveWait] = useState(false);
    const [approachPitch, setApproachPitch] = useState(defaultObject);
    const [matchingVideos, setMatchingVideos] = useState(null);
    const validPfxType = approachPitch?.FXType != null;
    const [pitchLocationRaw, setPitchLocationRaw] = useState([]);
    const validPitchLocation = approachPitch?.LocationX && approachPitch?.LocationY;

    const StartEnd = { START: 'start', END: 'end' };
    const startEndOptions = [
        { name: translate('sections.opponent.player.pitch-start'), value: StartEnd.START, variant: 'default' },
        { name: translate('sections.opponent.player.pitch-end'), value: StartEnd.END, variant: 'default' },
    ];

    const saveComplete = (isDelete, saveData, videos) => {
        onSave(isDelete, { ...saveData, videos });
    };

    const { mutate: saveApproachPitch, isLoading: savingApproachPitch } = useOktaMutation(
        (t) => Approach.update(t, { userName, batterId, pitcherId, ...approachPitch }),
        { onSuccess: (data) => saveComplete(false, data, matchingVideos) },
    );

    const { mutate: deleteApproachPitch, isLoading: deletingApproachPitch } = useOktaMutation(
        (t) => Approach.delete(t, approachPitch.ApproachPitchID),
        { onSuccess: () => saveComplete(true, { ApproachPitchID: approachPitch.ApproachPitchID }, []) },
    );

    const videoSearchComplete = async (serchResult) => {
        const { videos } = serchResult;
        // if there are not results, mention it to the user and require them to make edits
        if (videos.length === 0) {
            setMatchingVideos([]);
            setSaveWait(false);
        } else {
            // if there are, issue a save on this object to the DB, then pass the results to the tab and close the modal
            setMatchingVideos(videos);
        }
    };

    const { isFetching: loadingVideos, refetch: refetchVideos } = useOktaQuery(
        ['videos', { batterId, pitcherId, approachPitch }],
        (t) => Video.search(t, { approachTab: true, batterId, pitcherId, fxpitchtype: approachPitch.FXType, LocationX: approachPitch.LocationX, LocationY: approachPitch.LocationY, IsPitchStart: approachPitch.IsPitchStart }),
        {
            enabled: false,
            onSuccess: (result) => videoSearchComplete(result),
        },
    );

    // this trigger only happens after a successful video search/save
    useEffect(() => {
        if (matchingVideos && matchingVideos.length > 0) {
            saveApproachPitch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchingVideos]);

    useEffect(() => {
        // Reset our known state for each time we open the modal
        if (pitchToUpdate) {
            setApproachPitch(pitchToUpdate);
            // also set the pitch location
            if (pitchToUpdate.LocationX && pitchToUpdate.LocationY) {
                // if we have a location on update, set it into the component
                setPitchLocationRaw([{ locationx: pitchToUpdate.LocationX, locationy: pitchToUpdate.LocationY }]);
            }
        } else {
            setApproachPitch(defaultObject);
            setPitchLocationRaw([]);
        }
        setMatchingVideos(null);
        setSaveWait(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        // Reset this message upon interaction
        setMatchingVideos(null);
    }, [approachPitch]);

    const saveButton = () => {
        setSaveWait(true);
        // attempt a video search and make sure there are results
        refetchVideos();
    };

    const checkSavePitchLocation = (location) => {
        setPitchLocationRaw(location);
        if (location && location.length === 1) {
            setApproachPitch({ ...approachPitch, LocationX: location[0].locationx, LocationY: location[0].locationy });
        } else {
            setApproachPitch({ ...approachPitch, LocationX: 0, LocationY: 0 });
        }
    };

    const saveDisabled = () => saveWait || !validPitchLocation || !validPfxType || deletingApproachPitch;
    const cancelDeleteDisabled = () => saveWait || deletingApproachPitch;

    return (
        <BaseModal size="md" container={container} show={show} onHide={onHide} backdrop="static" centered>
            <BaseModal.Body className="left">
                <h3>{translate('sections.opponent.player.add-pitch')}</h3>
                <hr />
                <Row className="text-center">
                    <Col md={12}>
                        {fxpitchTypes.map((fxpitch) => <StyledPitchButton key={fxpitch} variant={fxpitch === approachPitch.FXType ? 'primary' : 'default'} onClick={() => setApproachPitch({ ...approachPitch, FXType: fxpitch })}>{pitchType(fxpitch, undefined, undefined, translate)}</StyledPitchButton>)}
                        {!validPfxType && <><br /><StyledErrorMesssage>{translate('sections.opponent.player.please-select-pitch')}</StyledErrorMesssage></>}
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col md={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}>{translate('sections.opponent.player.3b-side')}</Col>
                    <Col md={6} className="text-center">
                        <div>&nbsp;<br /><br /></div>
                        <PitchLocation
                            locations={pitchLocationRaw}
                            onChange={(value) => checkSavePitchLocation(value)}
                            singleSelectMode
                        />
                        <br />
                        <img src={plate} alt="Home Plate" height={20} width={50} style={{ marginTop: '10px' }} />
                        {!validPitchLocation && <><br /><br /><StyledErrorMesssage>{translate('sections.opponent.player.please-select-single-pitch')}</StyledErrorMesssage></>}
                    </Col>
                    <Col md={3} style={{ display: 'flex', alignItems: 'center' }}>{translate('sections.opponent.player.1b-side')}</Col>
                </Row>
                <hr />
                {/* Button state is tricky when used in a form, so we have to repeat this control for the two cases */}
                {approachPitch && approachPitch.IsPitchStart && (
                    <StyledStartEndContainer
                        name="toggle"
                        size="sm"
                        options={startEndOptions}
                        onChange={(value) => setApproachPitch({ ...approachPitch, IsPitchStart: value === StartEnd.START })}
                        defaultValue={StartEnd.START}
                    />
                )}
                {approachPitch && !approachPitch.IsPitchStart && (
                    <StyledStartEndContainer
                        name="toggle"
                        size="sm"
                        options={startEndOptions}
                        onChange={(value) => setApproachPitch({ ...approachPitch, IsPitchStart: value === StartEnd.START })}
                        defaultValue={StartEnd.END}
                    />
                )}
                <Row>
                    <Col md={12}>
                        {loadingVideos && <> <hr /><StyledMesssage><LoadingSpinner />&nbsp;{translate('sections.opponent.player.searching-videos-please-wait')}</StyledMesssage></>}
                        {savingApproachPitch && <> <hr /><StyledMesssage><LoadingSpinner />&nbsp;{translate('sections.opponent.player.saving-search-please-wait', { value: matchingVideos?.length })}</StyledMesssage></>}
                        {deletingApproachPitch && <> <hr /><StyledMesssage><LoadingSpinner />&nbsp;{translate('sections.opponent.player.deleting-please-wait')}</StyledMesssage></>}
                        {matchingVideos && matchingVideos.length === 0 && <> <hr /><StyledErrorMesssage style={{ fontWeight: 'bold' }}>{translate('sections.opponent.player.no-matching-videos')}. {translate('sections.opponent.player.change-try-again')}.</StyledErrorMesssage></>}
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col>
                        {approachPitch.ApproachPitchID > 0 && <Button variant="danger" onClick={() => deleteApproachPitch()} disabled={cancelDeleteDisabled()}>{translate('common.delete')}</Button>}
                    </Col>
                    <Col style={{ textAlign: 'end' }}>
                        <Button variant="secondary" onClick={onHide} disabled={cancelDeleteDisabled()}>{translate('common.cancel')}</Button>
                        <Button variant="primary" onClick={() => saveButton()} disabled={saveDisabled()} style={{ marginLeft: '5px' }}>{translate('common.save')}</Button>
                    </Col>
                </Row>
            </BaseModal.Body>
        </BaseModal>
    );
};

ApproachModal.propTypes = {
    onSave: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired,
    show: PropTypes.bool,
    container: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    pitcherId: PropTypes.number.isRequired,
    batterId: PropTypes.number.isRequired,
    userName: PropTypes.string.isRequired,
    fxpitchTypes: PropTypes.arrayOf(number).isRequired,
    pitchToUpdate: PropTypes.shape({
        LocationX: PropTypes.number,
        LocationY: PropTypes.number,
    }),
};

ApproachModal.defaultProps = {
    show: false,
    container: undefined,
    pitchToUpdate: undefined,
};

export default ApproachModal;
